import { useState, type FormEvent, Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  HiChevronRight,
  HiUsers,
  HiCalendar,
  HiChartBar,
  HiCog,
  HiShieldCheck,
  HiCheckCircle,
  HiCash,
  HiClock,
} from 'react-icons/hi';
import FaqAccordion from '@/components/common/FaqAccordion/FaqAccordion';
import { useLanguage } from '@/contexts/LanguageContext';
import './ForLandlords.css';

const FAQ_ITEM_IDS = [
  'clubOwner',
  'scheduleOnly',
  'brandedApp',
  'advantages',
  'payments',
  'personalTrainer',
  'groupTrainer',
  'findGymForSection',
  'staffLimits',
  'clientComms',
  'cost',
  'existingCrm',
  'start',
] as const;

const COMPARE_GROUPS = [
  {
    key: 'channel',
    rows: [
      { key: 'sharedApp', crm: 'no', us: 'yes' },
      { key: 'brandedApp', crm: 'no', us: 'option' },
    ],
  },
  {
    key: 'parity',
    rows: [
      { key: 'journal', crm: 'yes', us: 'yes' },
      { key: 'online', crm: 'yes', us: 'yes' },
      { key: 'group', crm: 'yes', us: 'yes' },
      { key: 'personal', crm: 'yes', us: 'yes' },
      { key: 'timeline', crm: 'no', us: 'yes' },
      { key: 'split', crm: 'no', us: 'yes' },
      { key: 'sportsModel', crm: 'no', us: 'yes' },
      { key: 'clients', crm: 'yes', us: 'yes' },
      { key: 'walkin', crm: 'yes', us: 'yes' },
      { key: 'tariffs', crm: 'yes', us: 'yes' },
      { key: 'packs', crm: 'yes', us: 'yes' },
      { key: 'rental', crm: 'yes', us: 'yes' },
    ],
  },
] as const;

const UTP_GROUPS = [
  {
    key: 'schedule',
    items: ['split', 'extras', 'recurring', 'csv'],
  },
  {
    key: 'sections',
    items: ['groupPersonal', 'clients', 'appSignup', 'abonements', 'tariffs'],
  },
  {
    key: 'team',
    items: ['staff', 'notifications', 'reports'],
  },
] as const;

const STORIES = [
  { key: 'demand', image: '/images/for-landlords/story-demand.png' },
  { key: 'schedule', image: '/images/for-landlords/story-schedule.png' },
  { key: 'sections', image: '/images/for-landlords/story-sections.png' },
  { key: 'money', image: '/images/for-landlords/story-money.png' },
] as const;

export default function ForLandlords() {
  const { t } = useLanguage();
  const [utpTab, setUtpTab] = useState<(typeof UTP_GROUPS)[number]['key']>('schedule');
  const [brandedPhone, setBrandedPhone] = useState('');
  const [brandedEmail, setBrandedEmail] = useState('');
  const [brandedStatus, setBrandedStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const adminUrl = import.meta.env.PROD
    ? 'https://admin.sportsground.ru'
    : 'http://localhost:3000';
  const adminRegistrationUrl = import.meta.env.PROD
    ? 'https://admin.sportsground.ru/registration'
    : 'http://localhost:3000/registration';

  const activeUtpGroup = UTP_GROUPS.find((group) => group.key === utpTab) ?? UTP_GROUPS[0];

  const handleBrandedSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (brandedStatus === 'submitting') return;

    const form = event.currentTarget;
    const honeypot =
      (form.elements.namedItem('website') as HTMLInputElement | null)?.value?.trim() || '';

    setBrandedStatus('submitting');
    const apiBase =
      (import.meta.env?.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ||
      'https://api.sportsground.ru';

    try {
      const response = await fetch(`${apiBase}/webhook/branded-app-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: brandedPhone.trim(),
          email: brandedEmail.trim(),
          source: 'for-landlords',
          website: honeypot,
        }),
      });

      if (!response.ok) {
        throw new Error(`lead_http_${response.status}`);
      }

      setBrandedPhone('');
      setBrandedEmail('');
      setBrandedStatus('success');
    } catch {
      setBrandedStatus('error');
    }
  };

  const heroFeatures = [
    { icon: HiCalendar, titleKey: 'booking' },
    { icon: HiChartBar, titleKey: 'occupancy' },
    { icon: HiUsers, titleKey: 'clients' },
    { icon: HiCog, titleKey: 'manage' },
  ] as const;

  const jobCards = [
    {
      key: 'clients',
      title: t('home.landlords.benefits.moreClients.title'),
      description: t('home.landlords.benefits.moreClients.description'),
      image: '/images/for-landlords/job-clients.png',
    },
    {
      key: 'schedule',
      title: t('home.landlords.benefits.digitalSchedule.title'),
      description: t('home.landlords.benefits.digitalSchedule.description'),
      image: '/images/for-landlords/job-schedule.png',
    },
  ] as const;

  return (
    <div className="for-landlords-page">
      <section className="fl-hero">
        <div className="container">
          <div className="fl-hero-inner">
            <div className="fl-hero-copy">
              <span className="fl-hero-badge">
                <HiChartBar aria-hidden="true" />
                {t('forLandlords.hero.badge')}
              </span>

              <h1 className="fl-hero-title">
                {t('forLandlords.hero.title')}
                <span className="fl-hero-title-accent">{t('forLandlords.hero.titleAccent')}</span>
              </h1>

              <p className="fl-hero-subtitle">{t('forLandlords.hero.subtitle')}</p>

              <div className="fl-hero-features">
                {heroFeatures.map(({ icon: Icon, titleKey }) => (
                  <div key={titleKey} className="fl-hero-feature">
                    <span className="fl-hero-feature-icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <span className="fl-hero-feature-text">
                      <span className="fl-hero-feature-title">
                        {t(`forLandlords.hero.features.${titleKey}.title`)}
                      </span>
                      <span className="fl-hero-feature-desc">
                        {t(`forLandlords.hero.features.${titleKey}.text`)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="fl-hero-actions">
                <div className="fl-hero-actions-row">
                  <a
                    href={adminRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fl-btn fl-btn--primary"
                  >
                    {t('home.landlords.connectCta')}
                    <HiChevronRight aria-hidden="true" />
                  </a>
                  <a href="#fl-stories" className="fl-btn fl-btn--secondary">
                    {t('forLandlords.learnMore')}
                  </a>
                </div>
                <a
                  href={adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fl-btn fl-btn--admin"
                >
                  <HiShieldCheck aria-hidden="true" />
                  {t('common.enterAdmin')}
                </a>
              </div>
            </div>

            <div className="fl-hero-visual">
              <img
                src="/images/stand.png"
                alt={t('home.adminPanelImageAlt')}
                className="fl-hero-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="fl-jobs" id="fl-jobs">
        <div className="container">
          <h2 className="fl-section-title">{t('forLandlords.jobs.title')}</h2>
          <div className="fl-jobs-grid">
            {jobCards.map((card) => (
              <article key={card.key} className={`fl-job-card fl-job-card--${card.key}`}>
                <div className="fl-job-card-content">
                  <h3 className="fl-job-card-title">{card.title}</h3>
                  <p className="fl-job-card-text">{card.description}</p>
                </div>
                <div className="fl-job-card-art" aria-hidden="true">
                  <img
                    src={card.image}
                    alt=""
                    className="fl-job-card-image"
                    loading="lazy"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="fl-stories" id="fl-stories">
        <div className="container">
          <h2 className="fl-section-title">{t('forLandlords.stories.title')}</h2>
          <p className="fl-section-subtitle">{t('forLandlords.stories.subtitle')}</p>
          <div className="fl-stories-list">
            {STORIES.map(({ key, image }) => (
              <article key={key} className="fl-story">
                <div className="fl-story-problem">
                  <span className="fl-story-label">{t('forLandlords.stories.problemLabel')}</span>
                  <h3 className="fl-story-heading">
                    {t(`forLandlords.stories.items.${key}.problemTitle`)}
                  </h3>
                  <p className="fl-story-body">
                    {t(`forLandlords.stories.items.${key}.problemText`)}
                  </p>
                </div>

                <div className="fl-story-solution">
                  <span className="fl-story-label">{t('forLandlords.stories.solutionLabel')}</span>
                  <h3 className="fl-story-heading">
                    {t(`forLandlords.stories.items.${key}.solutionTitle`)}
                  </h3>
                  <p className="fl-story-body">
                    {t(`forLandlords.stories.items.${key}.solutionText`)}
                  </p>
                  <a href="#fl-utp" className="fl-story-more">
                    {t('forLandlords.learnMore')}
                    <HiChevronRight aria-hidden="true" />
                  </a>
                </div>

                <div className="fl-story-media">
                  <img
                    src={image}
                    alt={t(`forLandlords.stories.items.${key}.solutionTitle`)}
                    className="fl-story-image"
                    loading="lazy"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="fl-mid-cta" id="fl-mid-cta">
        <div className="container">
          <div className="fl-mid-cta-inner">
            <h2 className="fl-cta-title">{t('forLandlords.midCta.title')}</h2>
            <p className="fl-cta-text">{t('forLandlords.midCta.subtitle')}</p>
            <div className="fl-hero-actions fl-hero-actions--center">
              <div className="fl-hero-actions-row">
                <a
                  href={adminRegistrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fl-btn fl-btn--primary"
                >
                  {t('home.landlords.connectCta')}
                  <HiChevronRight aria-hidden="true" />
                </a>
                <a
                  href={adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fl-btn fl-btn--admin"
                >
                  <HiShieldCheck aria-hidden="true" />
                  {t('common.enterAdmin')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="fl-utp" id="fl-utp">
        <div className="container">
          <div className="fl-utp-head">
            <h2 className="fl-section-title">{t('forLandlords.utp.title')}</h2>
            <p className="fl-utp-lead">{t('forLandlords.utp.free')}</p>
          </div>
          <div className="fl-utp-card">
            <div
              className="fl-utp-tabs"
              role="tablist"
              aria-label={t('forLandlords.utp.title')}
            >
              {UTP_GROUPS.map(({ key }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`fl-utp-tab-${key}`}
                  aria-selected={utpTab === key}
                  aria-controls={`fl-utp-panel-${key}`}
                  className={`fl-utp-tab${utpTab === key ? ' is-active' : ''}`}
                  onClick={() => setUtpTab(key)}
                >
                  {t(`forLandlords.utp.groups.${key}.title`)}
                </button>
              ))}
            </div>
            <div
              className="fl-utp-panel"
              role="tabpanel"
              id={`fl-utp-panel-${activeUtpGroup.key}`}
              aria-labelledby={`fl-utp-tab-${activeUtpGroup.key}`}
            >
              <ul
                key={activeUtpGroup.key}
                className="fl-feature-list fl-feature-list--checks fl-utp-list"
              >
                {activeUtpGroup.items.map((itemKey) => (
                  <li key={itemKey}>
                    <HiCheckCircle className="fl-feature-check" aria-hidden="true" />
                    <span>
                      {t(`forLandlords.utp.groups.${activeUtpGroup.key}.items.${itemKey}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="fl-utp-price">
              <div className="fl-utp-price-copy">
                <h3 className="fl-utp-price-title">{t('forLandlords.utp.price.title')}</h3>
                <div className="fl-utp-price-actions">
                  <a
                    href={adminRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fl-btn fl-btn--primary"
                  >
                    {t('home.landlords.connectCta')}
                    <HiChevronRight aria-hidden="true" />
                  </a>
                </div>
              </div>
              <div className="fl-utp-price-sum">
                <span className="fl-utp-price-value">{t('forLandlords.utp.price.value')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="fl-extras" id="fl-extras">
        <div className="container">
          <div className="fl-branded">
            <div className="fl-branded-main">
              <div className="fl-branded-copy">
                <span className="fl-branded-badge">{t('forLandlords.brandedAppForm.badge')}</span>
                <h2 className="fl-branded-title">
                  {t('forLandlords.brandedAppForm.title')}
                </h2>
                <p className="fl-branded-text">
                  {t('forLandlords.brandedAppForm.text')}
                </p>
                <form className="fl-branded-form" onSubmit={handleBrandedSubmit}>
                  <label className="fl-branded-field fl-branded-honeypot" aria-hidden="true">
                    <span>Website</span>
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
                  </label>
                  <label className="fl-branded-field">
                    <span>{t('forLandlords.brandedAppForm.phone')}</span>
                    <input
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      required
                      disabled={brandedStatus === 'submitting'}
                      placeholder={t('forLandlords.brandedAppForm.phonePlaceholder')}
                      value={brandedPhone}
                      onChange={(event) => {
                        setBrandedPhone(event.target.value);
                        if (brandedStatus === 'error' || brandedStatus === 'success') {
                          setBrandedStatus('idle');
                        }
                      }}
                    />
                  </label>
                  <label className="fl-branded-field">
                    <span>{t('forLandlords.brandedAppForm.email')}</span>
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                      disabled={brandedStatus === 'submitting'}
                      placeholder={t('forLandlords.brandedAppForm.emailPlaceholder')}
                      value={brandedEmail}
                      onChange={(event) => {
                        setBrandedEmail(event.target.value);
                        if (brandedStatus === 'error' || brandedStatus === 'success') {
                          setBrandedStatus('idle');
                        }
                      }}
                    />
                  </label>
                  <button
                    type="submit"
                    className="fl-btn fl-btn--branded"
                    disabled={brandedStatus === 'submitting'}
                  >
                    {brandedStatus === 'submitting'
                      ? t('forLandlords.brandedAppForm.submitting')
                      : t('forLandlords.brandedAppForm.submit')}
                  </button>
                  {brandedStatus === 'success' ? (
                    <p className="fl-branded-form-status fl-branded-form-status--ok" role="status">
                      {t('forLandlords.brandedAppForm.success')}
                    </p>
                  ) : null}
                  {brandedStatus === 'error' ? (
                    <p className="fl-branded-form-status fl-branded-form-status--err" role="alert">
                      {t('forLandlords.brandedAppForm.error')}
                    </p>
                  ) : null}
                </form>
              </div>
              <div className="fl-branded-visual">
                <ul className="fl-branded-plaque">
                  <li className="fl-branded-point fl-branded-point--accent">
                    <span className="fl-branded-point-icon" aria-hidden="true">
                      <HiCash />
                    </span>
                    <span className="fl-branded-point-copy">
                      <strong>{t('forLandlords.brandedAppForm.points.price.title')}</strong>
                      <span>{t('forLandlords.brandedAppForm.points.price.text')}</span>
                    </span>
                  </li>
                  <li className="fl-branded-point">
                    <span className="fl-branded-point-icon" aria-hidden="true">
                      <HiShieldCheck />
                    </span>
                    <span className="fl-branded-point-copy">
                      <strong>{t('forLandlords.brandedAppForm.points.terms.title')}</strong>
                      <span>{t('forLandlords.brandedAppForm.points.terms.text')}</span>
                    </span>
                  </li>
                  <li className="fl-branded-point">
                    <span className="fl-branded-point-icon" aria-hidden="true">
                      <HiClock />
                    </span>
                    <span className="fl-branded-point-copy">
                      <strong>{t('forLandlords.brandedAppForm.points.launch.title')}</strong>
                      <span>{t('forLandlords.brandedAppForm.points.launch.text')}</span>
                    </span>
                  </li>
                </ul>
                <img
                  src="/images/for-landlords/branded-app.png"
                  alt=""
                  className="fl-branded-phone"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="fl-compare" id="fl-compare">
        <div className="container">
          <h2 className="fl-section-title">{t('forLandlords.compare.title')}</h2>
          <p className="fl-section-subtitle">{t('forLandlords.compare.subtitle')}</p>
          <div className="fl-compare-table-wrap">
            <table className="fl-compare-table">
              <thead>
                <tr>
                  <th scope="col">{t('forLandlords.compare.colFeature')}</th>
                  <th scope="col">{t('forLandlords.compare.colCrm')}</th>
                  <th scope="col" className="fl-compare-col-us">
                    {t('forLandlords.compare.colUs')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_GROUPS.map((group) => (
                  <Fragment key={group.key}>
                    <tr className="fl-compare-group">
                      <th scope="colgroup" colSpan={3}>
                        {t(`forLandlords.compare.groups.${group.key}.title`)}
                      </th>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.key}>
                        <th scope="row">
                          {t(`forLandlords.compare.groups.${group.key}.rows.${row.key}`)}
                        </th>
                        <td>
                          <span
                            className={`fl-compare-status fl-compare-status--${row.crm}`}
                          >
                            {t(`forLandlords.compare.status.${row.crm}`)}
                          </span>
                        </td>
                        <td className="fl-compare-col-us">
                          <span
                            className={`fl-compare-status fl-compare-status--${row.us}`}
                          >
                            {t(`forLandlords.compare.status.${row.us}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fl-compare-note">{t('forLandlords.compare.note')}</p>
        </div>
      </section>

      <section className="fl-faq">
        <div className="container">
          <FaqAccordion
            titleKey="forLandlords.faq.title"
            namespace="forLandlords.faq"
            itemIds={[...FAQ_ITEM_IDS]}
          />
        </div>
      </section>

      <section className="fl-cta">
        <div className="container">
          <div className="fl-cta-banner">
            <div className="fl-cta-card">
              <div className="fl-cta-card-copy">
                <h2 className="fl-cta-title">{t('forLandlords.cta.title')}</h2>
                <p className="fl-cta-text">{t('forLandlords.cta.subtitle')}</p>
                <div className="fl-cta-card-actions">
                  <a
                    href={adminRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fl-cta-card-cta fl-cta-card-cta--primary"
                  >
                    {t('home.landlords.connectCta')}
                    <HiChevronRight aria-hidden="true" />
                  </a>
                  <a
                    href={adminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fl-cta-card-cta fl-cta-card-cta--secondary"
                  >
                    {t('common.enterAdmin')}
                  </a>
                  <Link to="/" className="fl-cta-card-cta fl-cta-card-cta--secondary">
                    {t('common.backToHome')}
                  </Link>
                </div>
              </div>
              <img
                src="/images/landlords.jpg"
                alt={t('home.landlords.imageAlt')}
                className="fl-cta-card-image"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
